'use strict';

import {Fancybox} from '@fancyapps/ui/src/Fancybox/Fancybox.js';
import FancyboxDe from '@fancyapps/ui/src/Fancybox/l10n/de';
import Comment from './comment.js';
import PictureShow from './picture-show.js';

export default class AppFancyBox {
    initialize() {
        this.defaults();
        this.plugins();
        this.bindFancyBoxPicture();
    }

    defaults() {
        Fancybox.defaults.Hash = false;
        Fancybox.defaults.l10n = FancyboxDe;
        Fancybox.defaults.autoFocus = false; // Set focus on first focusable element after displaying content
        Fancybox.defaults.trapFocus = false; // Trap focus inside Fancybox
    // Keep the Fancybox toolbar (incl. close button) always visible
    Fancybox.defaults.idle = false;
    // Click on content (image) should close the viewer
    Fancybox.defaults.contentClick = 'close';
    Fancybox.defaults.contentDblClick = false;
    }

    plugins() {
        Fancybox.Plugins.Toolbar.defaults.items.openInNewWindow = {
            type: 'button',
            class: 'fancybox__button--openInNewWindow',
            label: 'Zur Seite',
            html: '<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="external-link-alt" class="svg-inline--fa fa-external-link-alt fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z"></path></svg>',
            click: (fancybox) => {
                const slide = fancybox.getSlide();
                if (!slide) {
                    return;
                }
                const url = slide.$trigger?.dataset?.url || slide.src;
                if (url) {
                    window.open(url, '_blank');
                }
            },
        };
    }

    bindFancyBoxPicture(callbackLoad) {
        const comment = new Comment();
        Fancybox.bind('[data-fancybox]', {
            preload: 1,
            infinite: false,
            contentClick: 'close',

            groupAll: true,
            on: {
                'Carousel.createSlide': (fancybox, carousel, slide) => {
                    // If provided, allow caller to trigger on last slide creation
                    if (callbackLoad instanceof Function && slide.index === fancybox.items.length - 1) {
                        callbackLoad(fancybox, carousel, slide);
                    }

                    const pictureShow = new PictureShow();
                    (async function waitForDone(s) {
                        let retry = 0;
                        const interval = setInterval(() => {
                            if (s.state === 'done') {
                                clearInterval(interval);

                                const show = s.$el.querySelector('.js-picture-show');
                                if (show) {
                                    pictureShow.initializeElement(show);
                                }

                                const jsComment = s.$el.querySelector('.js-comment');
                                if (jsComment) {
                                    comment.initializeElement(null, jsComment);
                                }
                            }

                            // Safety: stop polling after ~100s
                            if (retry >= 200) {
                                clearInterval(interval);
                            }
                            retry++;
                        }, 500);
                        return 0;
                    })(slide);
                },
                done: (fancybox, slide) => {
                    // Helper to re-arm "show-once" auto-hide for an element
                    const rearmAutoHide = (el, duration = 3500) => {
                        if (!el) return;
                        el.classList.add('auto-hide');
                        el.classList.add('show-once');
                        if (el.__hideTimer) { clearTimeout(el.__hideTimer); }
                        el.__hideTimer = setTimeout(() => {
                            el.classList.remove('show-once');
                            el.__hideTimer = null;
                        }, duration);
                    };

                    // Click image to close
                    const img = slide.$el?.querySelector?.('img');
                    if (img) {
                        img.addEventListener('click', (e) => {
                            e.stopPropagation();
                            fancybox.close();
                        });
                    }

                    // Prevent anchors that point to image files from navigating away
                    const anchors = slide.$el?.querySelectorAll?.('a[href]') || [];
                    anchors.forEach((a) => {
                        const href = a.getAttribute('href') || '';
                        if (/\.(jpe?g|png|gif|webp|avif)(\?.*)?$/i.test(href)) {
                            a.addEventListener('click', (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            });
                        }
                    });

                    // Keep overlay toolbar visible inside lightbox, and protect overlay link click
                    const show = slide.$el?.querySelector?.('.js-picture-show .picture');
                    if (show) {
                        show.classList.remove('invert');
                        const overlayLink = show.querySelector('.overlay-link');
                        if (overlayLink) {
                            // If this overlay is meant to open a zoom (has data-fancybox), let it open without closing the viewer
                            overlayLink.addEventListener('click', (e) => {
                                // If no fancybox target on overlay-link, treat it as a close action
                                const hasTarget = overlayLink.hasAttribute('data-fancybox') || overlayLink.hasAttribute('data-src');
                                if (!hasTarget) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    fancybox.close();
                                } else {
                                    // Prevent closing current viewer due to bubbling when it opens nested/target fancybox
                                    e.stopPropagation();
                                    // Re-arm the image overlay to fade after showing once
                                    const imageOverlay = show.closest('.image-overlay') || show.querySelector('.image-overlay');
                                    if (imageOverlay) {
                                        rearmAutoHide(imageOverlay);
                                    }
                                }
                            });
                        }
                    }

                    // Add a bottom close overlay inside Fancybox (separate from magnifier overlay on detail page)
                    const mount = slide.$el?.querySelector?.('.fancybox__content') || slide.$el;
                    if (mount && !mount.querySelector('.sb-fb-overlay-close')) {
                        const closeBar = document.createElement('div');
                        closeBar.className = 'sb-fb-overlay-close auto-hide show-once';
                        closeBar.setAttribute('title', FancyboxDe.CLOSE || 'Schließen');
                        closeBar.innerHTML = '<span class="sb-fb-close-inner"><i class="fas fa-times"></i><span>Schließen</span></span>';
                        closeBar.addEventListener('click', (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            fancybox.close();
                        });
                        mount.appendChild(closeBar);
                        // Remove show-once after a few seconds to keep hover-only reveal
                        setTimeout(() => closeBar.classList.remove('show-once'), 4000);

                        // Tap/gesture should show the bar briefly, then auto-hide again
                        const armFromEvent = (ev) => {
                            const target = ev.target;
                            if (target && (target.closest('.fancybox__toolbar') || target.closest('.sb-fb-overlay-close'))) {
                                return; // ignore interactions on UI
                            }
                            rearmAutoHide(closeBar);
                        };
                        // Pointer on content area
                        mount.addEventListener('pointerdown', armFromEvent, { passive: true });
                        // Wheel/scroll (desktop zoom)
                        mount.addEventListener('wheel', armFromEvent, { passive: true });
                        // Panzoom hooks if available
                        try {
                            if (slide.Panzoom && slide.Panzoom.on) {
                                slide.Panzoom.on('start', () => rearmAutoHide(closeBar));
                                slide.Panzoom.on('afterUpdate', () => rearmAutoHide(closeBar));
                            }
                        } catch (e) {}
                    }
                },
            },

            Thumbs: {
                autoStart: false,
                Carousel: { fill: true, center: true },
            },

            Toolbar: {
                autoEnable: true,
                display: [
                    { id: 'prev', position: 'left' },
                    { id: 'counter', position: 'left' },
                    { id: 'next', position: 'left' },

                    { id: 'openInNewWindow', position: 'right' },
                    { id: 'slideshow', position: 'right' },
                    { id: 'thumbs', position: 'right' },
                    { id: 'fullscreen', position: 'right' },
                    { id: 'zoom', position: 'right' },
                    { id: 'download', position: 'right' },
                    { id: 'close', position: 'right' },
                ],
            },

            // Keep context: do not change page when closing
            hideScrollbar: false,
            trapFocus: true,
        });
    }
}
